import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useForm, FormProvider } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { giftVoucherFormSchema } from 'utils/form-validation-schema';
import { DropdownWithMenu } from 'components/UI/DropdownWithMenu/DropdownWithMenu';
import { InputControlled } from 'components/UI/InputControlled/InputControlled';
import { TextAreaControlled } from 'components/UI/TextArea/TextArea';
import { Callout } from 'components/UI/Callout/Callout';
import { addGiftCertificate } from 'services/AddGiftVoucherService';
import { shapeAddGiftVoucherPayload } from './gift-voucher-utils';

export const GiftVouchers = (props) => {
  const { GiftCertificateAmounts } = props;
  const [selectedAmount, setSelectedAmount] = useState('');
  const [selectedAmountDesc, setSelectedAmountDesc] = useState('Select Amount');
  const [errorMessage, setErrorMessage] = useState('');
  const [amountErrorMessage, setAmountErrorMessage] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [otherAmount, setOtherAmount] = useState(false);
  const [hasNoAmount, setHasNoAmount] = useState(false);

  const methods = useForm({
    criteriaMode: 'all',
    mode: 'onChange',
    resolver: yupResolver(giftVoucherFormSchema),
  });

  const {
    formState: { isValid },
    resetField,
    handleSubmit,
    setValue,
  } = methods;

  const amountDropDownOptions = GiftCertificateAmounts.map((item) => ({
    key: item.Key,
    name: item.Value,
  }));

  const amountDropdownUpdateHandler = (amount) => {
    setOtherAmount('');
    setSelectedAmount(amount.toString());

    const amountDesc = GiftCertificateAmounts.find((item) => item.Key === amount);
    setSelectedAmountDesc(amountDesc.Value.toString());

    resetField('OtherAmount');
  };

  const otherAmountClickHandler = () => {
    setSelectedAmount('');
    setSelectedAmountDesc('Select Amount');
  };

  const otherAmountChangeValueHandler = (otherAmountValue) => {
    setOtherAmount(otherAmountValue);
    /**
     * Because the input of type number does not trigger onChange when
     * entered an 'e' or 'E' (exponential) character, we are using
     * input of type text
     *
     * Replace non-numeric value to empty string
     */
    const numericAndExponentialRegex = /^[\d.\s?e+]+$/i;
    if (!numericAndExponentialRegex.test(otherAmountValue)) {
      setValue('OtherAmount', '');
      setOtherAmount('');
    }

    /**
     * Replace repeating e and E with single character
     */
    if (/e{2,}|eE/g.test(otherAmountValue)) {
      setValue('OtherAmount', 'e');
      setOtherAmount('e');
    }
    if (/E{2,}|Ee/g.test(otherAmountValue)) {
      setValue('OtherAmount', 'E');
      setOtherAmount('E');
    }
  };

  useEffect(() => {
    setHasNoAmount(!selectedAmount && !otherAmount);
  }, [selectedAmount, otherAmount]);

  const giftVouchersFormSubmitHandler = async (inputsData) => {
    setIsProcessing(true);

    if (hasNoAmount) {
      setAmountErrorMessage('Please select an amount or enter your own amount.');
      setIsProcessing(false);
      return;
    }

    const requestPayload = shapeAddGiftVoucherPayload({
      payloadFromSC: props,
      inputsData,
      dropdownsData: {
        selectedAmount,
        selectedAmountDesc,
      },
    });

    const response = await addGiftCertificate(requestPayload);

    if (response?.Redirect) {
      window.location.href = response.RedirectUrl;
    } else {
      setIsProcessing(false);
    }
  };

  const submitButtonClickHandler = () => {
    if (!isValid) {
      setErrorMessage('Please complete the gift voucher form.');
    }
  };

  return (
    <div className="container">
      <div className="row">
        <div className="col-sm-8 col-sm-offset-1">
          <p>
            <small className="form-disclaim">All fields required unless marked otherwise.</small>
          </p>
          <br />
          <FormProvider {...methods}>
            <form
              name="giftCertificatesForm"
              noValidate="novalidate"
              onSubmit={handleSubmit(giftVouchersFormSubmitHandler)}
            >
              <div className="form-section">
                <h3>Gift voucher value</h3>
                <div className="form-group">
                  <div className="input-container">
                    <label className="control-label static-label">
                      Select a gift voucher amount
                    </label>
                    <DropdownWithMenu
                      singleColumn
                      hideDefaultItem
                      dropdownChangeHandler={amountDropdownUpdateHandler}
                      defaultChosenValue={selectedAmountDesc}
                      defaultValue={selectedAmount}
                      menuData={amountDropDownOptions}
                      updatedChosenValue={selectedAmount}
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label htmlFor="OtherAmount" className="control-label static-label">
                    OR enter your own (min $10 max $500 AUD)
                  </label>
                  <div className="input-container">
                    <InputControlled
                      type="text"
                      id="OtherAmount"
                      labelText="$ other amount"
                      onFocusInputHandler={otherAmountClickHandler}
                      onChangeValueHandler={otherAmountChangeValueHandler}
                      min={10}
                      max={500}
                    />
                  </div>
                </div>

                {amountErrorMessage && hasNoAmount && <Callout errorMessage={amountErrorMessage} />}
              </div>

              <div className="form-section">
                <h3>Gift voucher recipient</h3>

                <div className="form-user-details">
                  <div className="input-container">
                    <div className="form-group">
                      <InputControlled
                        type="text"
                        id="EmailAddress"
                        labelText="Recipient's email address:"
                        hideErrorMsgForRequired
                      />
                    </div>

                    <div className="form-group">
                      <InputControlled
                        type="text"
                        id="ConfirmEmailAddress"
                        labelText="Confirm recipient's email address"
                        showAllErrorMessages
                        hideErrorMsgForRequired
                      />
                    </div>

                    <div className="form-group">
                      <div className="input-container">
                        <TextAreaControlled
                          cols="20"
                          id="Message"
                          rows="6"
                          labelText="Personal message"
                          maxLength="500"
                        />

                        <p data-testid="note-text">
                          <small className="form-disclaim">
                            Note: The gift voucher will be sent from Arts Centre Melbourne's box
                            office email account, so it's a good idea to identify yourself by your
                            name / nickname in your message to the lucky recipient! Max. 500
                            characters.
                          </small>
                        </p>
                      </div>
                    </div>
                  </div>

                  {errorMessage && <Callout errorMessage={errorMessage} />}

                  <button
                    type="submit"
                    className="btn btn-primary"
                    onClick={submitButtonClickHandler}
                    disabled={isProcessing || !isValid || hasNoAmount}
                    data-testid="add-to-cart-btn"
                  >
                    <span>{isProcessing ? 'Processing...' : 'Add To Cart'}</span>
                  </button>
                </div>
              </div>
            </form>
          </FormProvider>
        </div>
      </div>
    </div>
  );
};

GiftVouchers.propTypes = {
  GiftCertificateAmounts: PropTypes.array,
};

export const GiftVouchersSC = (props) => {
  const { sitecoreData } = props;
  return <GiftVouchers {...sitecoreData} />;
};

GiftVouchersSC.propTypes = {
  sitecoreData: PropTypes.object,
};
